/**
 * Sync Queue Processor
 *
 * Processes pending sync queue items in order.
 * Handles retries, error recovery, and progress reporting.
 */

import { offlineDb } from '../database/offlineDatabase';
import useSyncStore from '../stores/syncStore';
import { getOfflineService } from '../services';
import type {
  SyncQueueItem,
  SyncQueueResult,
  EntityType,
  DEFAULT_BATCH_SIZE,
} from '../types/offline.types';
import { DEFAULT_BATCH_SIZE as BATCH_SIZE } from '../types/offline.types';

// ============================================
// SYNC QUEUE PROCESSOR
// ============================================

/**
 * Sync Queue Processor
 * Processes pending operations from the sync queue
 */
export class SyncQueueProcessor {
  private isProcessing = false;
  private abortController: AbortController | null = null;
  private processingPromise: Promise<SyncQueueResult> | null = null;

  // ============================================
  // PUBLIC METHODS
  // ============================================

  /**
   * Process the entire sync queue
   * Returns result with success/failure counts
   */
  async processQueue(): Promise<SyncQueueResult> {
    // If already processing, return the existing promise
    if (this.isProcessing && this.processingPromise) {
      return this.processingPromise;
    }

    // Check if online
    if (!navigator.onLine) {
      console.log('[SyncQueue] Cannot process - device is offline');
      return this.createEmptyResult();
    }

    this.processingPromise = this.doProcessQueue();
    return this.processingPromise;
  }

  /**
   * Stop processing the queue
   */
  abort(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    this.isProcessing = false;
  }

  /**
   * Check if queue is currently being processed
   */
  isActive(): boolean {
    return this.isProcessing;
  }

  // ============================================
  // PRIVATE METHODS
  // ============================================

  private async doProcessQueue(): Promise<SyncQueueResult> {
    this.isProcessing = true;
    this.abortController = new AbortController();

    const syncActions = useSyncStore.getState().actions;
    syncActions.startSync();

    const result: SyncQueueResult = {
      totalProcessed: 0,
      successCount: 0,
      failedCount: 0,
      conflictsResolved: 0,
      failures: [],
    };

    try {
      // Get all pending items
      const pendingItems = await this.getPendingItems();
      const totalItems = pendingItems.length;

      if (totalItems === 0) {
        console.log('[SyncQueue] No pending items to process');
        syncActions.endSync(true);
        return result;
      }

      console.log(`[SyncQueue] Processing ${totalItems} pending items`);

      // Group by entity type for ordered processing
      const groupedItems = this.groupByEntityType(pendingItems);

      // Process entity types in order (categories first, then others)
      const entityOrder: EntityType[] = [
        'transactionCategory',
        'branch',
        'contact',
        'employee',
        'inventory',
        'compositeItem',
        'transaction',
        'payable',
        'receivable',
      ];

      let processedCount = 0;

      for (const entityType of entityOrder) {
        const items = groupedItems.get(entityType);
        if (!items || items.length === 0) continue;

        if (this.abortController?.signal.aborted) {
          console.log('[SyncQueue] Processing aborted');
          break;
        }

        // Process items for this entity type
        const entityResult = await this.processEntityQueue(entityType, items);

        result.totalProcessed += entityResult.totalProcessed;
        result.successCount += entityResult.successCount;
        result.failedCount += entityResult.failedCount;
        result.conflictsResolved += entityResult.conflictsResolved;
        result.failures.push(...entityResult.failures);

        processedCount += items.length;
        const progress = Math.round((processedCount / totalItems) * 100);
        syncActions.updateProgress(progress);
      }

      syncActions.endSync(result.failedCount === 0);
      console.log('[SyncQueue] Processing completed:', result);

      return result;
    } catch (error) {
      console.error('[SyncQueue] Error processing queue:', error);
      syncActions.endSync(false);
      throw error;
    } finally {
      this.isProcessing = false;
      this.processingPromise = null;
      this.abortController = null;
    }
  }

  /**
   * Get all pending items sorted by creation time
   */
  private async getPendingItems(): Promise<SyncQueueItem[]> {
    return offlineDb.syncQueue
      .where('status')
      .equals('pending')
      .sortBy('createdAt');
  }

  /**
   * Group items by entity type
   */
  private groupByEntityType(items: SyncQueueItem[]): Map<EntityType, SyncQueueItem[]> {
    const grouped = new Map<EntityType, SyncQueueItem[]>();

    for (const item of items) {
      const existing = grouped.get(item.entityType) ?? [];
      existing.push(item);
      grouped.set(item.entityType, existing);
    }

    return grouped;
  }

  /**
   * Process items for a single entity type
   */
  private async processEntityQueue(
    entityType: EntityType,
    items: SyncQueueItem[]
  ): Promise<SyncQueueResult> {
    const result: SyncQueueResult = {
      totalProcessed: 0,
      successCount: 0,
      failedCount: 0,
      conflictsResolved: 0,
      failures: [],
    };

    // Sort by createdAt for proper ordering
    const sortedItems = [...items].sort((a, b) => a.createdAt - b.createdAt);

    // Try to get the offline service for this entity type
    let service: ReturnType<typeof getOfflineService> | null = null;
    try {
      service = getOfflineService(entityType as Parameters<typeof getOfflineService>[0]);
    } catch {
      console.warn(`[SyncQueue] No service available for entity type: ${entityType}`);
      // Mark all items as failed
      for (const item of sortedItems) {
        await this.markItemFailed(item, 'No offline service available');
        result.failures.push({
          queueItemId: item.id,
          entityType: item.entityType,
          error: 'No offline service available',
        });
      }
      result.totalProcessed = sortedItems.length;
      result.failedCount = sortedItems.length;
      return result;
    }

    // Process each item
    for (const item of sortedItems) {
      if (this.abortController?.signal.aborted) {
        break;
      }

      result.totalProcessed++;

      try {
        const syncResult = await service.processSyncQueueItem(item);

        if (syncResult.success) {
          result.successCount++;
          if (syncResult.conflictResolved) {
            result.conflictsResolved++;
          }
        } else {
          result.failedCount++;
          result.failures.push({
            queueItemId: item.id,
            entityType: item.entityType,
            error: syncResult.error ?? 'Unknown error',
          });
        }
      } catch (error) {
        result.failedCount++;
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        result.failures.push({
          queueItemId: item.id,
          entityType: item.entityType,
          error: errorMessage,
        });
      }

      // Small delay between items to avoid overwhelming the server
      await this.delay(50);
    }

    return result;
  }

  /**
   * Mark an item as failed
   */
  private async markItemFailed(item: SyncQueueItem, errorMessage: string): Promise<void> {
    await offlineDb.syncQueue.update(item.id, {
      status: 'failed',
      lastAttemptAt: Date.now(),
      errorMessage,
    });

    const syncActions = useSyncStore.getState().actions;
    syncActions.decrementPending(item.entityType);
    syncActions.incrementFailed(item.entityType);
  }

  /**
   * Create empty result
   */
  private createEmptyResult(): SyncQueueResult {
    return {
      totalProcessed: 0,
      successCount: 0,
      failedCount: 0,
      conflictsResolved: 0,
      failures: [],
    };
  }

  /**
   * Delay helper
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

// ============================================
// SINGLETON INSTANCE
// ============================================

export const syncQueueProcessor = new SyncQueueProcessor();
